import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from 'chart.js';
import KpiCard from './KpiCard';
import { VALET, FONTS, TYPE } from '../../theme';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

// Palette tuned to the editorial light theme — deeper, less candy.
const SVC_COLORS = [VALET.green, VALET.bronze, VALET.info, VALET.warning];
const SVC_LIGHT  = [VALET.greenLight, VALET.bronzeMuted, VALET.infoBg, VALET.warningBg];
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const SectionEyebrow = styled(Typography)({
  ...TYPE.eyebrow,
  marginBottom: '6px',
});

const SectionTitle = styled(Typography)({
  ...TYPE.sectionTitle,
  marginBottom: '16px',
});

const Grid4 = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '14px',
  marginBottom: '28px',
  '@media (max-width: 900px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
});

const Grid2 = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '14px',
  marginBottom: '28px',
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
  },
});

const Card = styled(Box)({
  backgroundColor: VALET.surface,
  border: `1px solid ${VALET.border}`,
  borderRadius: '14px',
  padding: '22px',
  boxShadow: VALET.shadow,
  '@media (max-width: 768px)': {
    padding: '16px 14px',
  },
});

const CardTitle = styled(Typography)({
  fontFamily: FONTS.display,
  fontSize: '15px',
  fontWeight: 500,
  letterSpacing: '-0.015em',
  color: VALET.ink,
  marginBottom: '14px',
});

const CHART_GRID = `${VALET.bronze}22`;
const CHART_TICK = VALET.textMuted;

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: VALET.ink,
      padding: 10,
      titleFont: { size: 11, family: 'Inter', weight: '600' },
      bodyFont: { size: 11, family: 'Inter' },
      cornerRadius: 6,
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: CHART_TICK, font: { size: 10, family: 'Inter' }, maxRotation: 0 },
    },
    y: {
      grid: { color: CHART_GRID },
      ticks: { color: CHART_TICK, font: { size: 10, family: 'Inter' } },
      border: { dash: [3, 3] },
    },
  },
};

function fmtK(n) {
  return n >= 1000 ? (n / 1000).toFixed(1) + 'k' : String(n);
}

function getHourLabel(i) {
  if (i % 4 !== 0) return '';
  if (i === 0) return '12am';
  if (i < 12) return i + 'am';
  if (i === 12) return '12pm';
  return (i - 12) + 'pm';
}

export default function UsersTab({ data }) {
  if (!data) return null;
  const d = data;

  const newUsers = d.newUsers || 0;
  const returningUsers = d.returningUsers || 0;
  const totalActive = d.totalActiveUsers || (newUsers + returningUsers);
  const returnRate = d.returnRate || (totalActive > 0 ? Math.round(returningUsers / totalActive * 100) + '%' : '0%');

  // Parse service type data — API uses _id for name
  let services = [];
  if (Array.isArray(d.requestsByServiceType)) {
    services = d.requestsByServiceType.map(item => ({
      name: item.serviceType || item._id || item.name || 'Unknown',
      count: item.count || item.value || 0,
    }));
  }
  const svcTotal = services.reduce((acc, s) => acc + s.count, 0);

  // Parse hourly data — API returns sparse { hour, count } objects, fill all 24 slots
  let byHour = new Array(24).fill(0);
  if (Array.isArray(d.requestsByHour)) {
    d.requestsByHour.forEach(item => {
      if (typeof item === 'number') return; // fallback for flat array
      const h = item.hour;
      if (h >= 0 && h < 24) byHour[h] = item.count || 0;
    });
    // If the array was flat numbers (not objects), use directly
    if (d.requestsByHour.length === 24 && typeof d.requestsByHour[0] === 'number') {
      byHour = d.requestsByHour;
    }
  }

  // Parse day-of-week data — API returns { day, count } objects
  let byDay = [];
  if (Array.isArray(d.requestsByDayOfWeek)) {
    if (typeof d.requestsByDayOfWeek[0] === 'number') {
      byDay = d.requestsByDayOfWeek;
    } else {
      // Map day names to ordered array
      const dayOrder = { Monday: 0, Tuesday: 1, Wednesday: 2, Thursday: 3, Friday: 4, Saturday: 5, Sunday: 6 };
      byDay = new Array(7).fill(0);
      d.requestsByDayOfWeek.forEach(item => {
        const idx = dayOrder[item.day] ?? dayOrder[item._id];
        if (idx !== undefined) byDay[idx] = item.count || 0;
      });
    }
  }

  const serviceChartData = {
    labels: services.map(s => s.name),
    datasets: [{
      data: services.map(s => s.count),
      backgroundColor: services.map((_, i) => SVC_COLORS[i % SVC_COLORS.length] + '33'),
      borderColor: services.map((_, i) => SVC_COLORS[i % SVC_COLORS.length]),
      borderWidth: 1.5,
      borderRadius: 2,
    }],
  };

  const hourChartData = {
    labels: Array.from({ length: 24 }, (_, i) => getHourLabel(i)),
    datasets: [{
      data: byHour,
      backgroundColor: `${VALET.green}22`,
      borderColor: VALET.green,
      borderWidth: 1.5,
      borderRadius: 2,
    }],
  };

  const dayChartData = {
    labels: DAYS,
    datasets: [{
      data: byDay,
      backgroundColor: byDay.map((_, i) => i === 4 || i === 5 ? `${VALET.bronze}40` : `${VALET.bronze}20`),
      borderColor: VALET.bronze,
      borderWidth: 1.5,
      borderRadius: 2,
    }],
  };

  return (
    <Box>
      <SectionEyebrow>Audience</SectionEyebrow>
      <SectionTitle>Who's using the platform</SectionTitle>
      <Grid4>
        <KpiCard label="New users" value={fmtK(newUsers)} subtitle={totalActive > 0 ? `${Math.round(newUsers / totalActive * 100)}% of active` : ''} />
        <KpiCard label="Returning users" value={fmtK(returningUsers)} subtitle={totalActive > 0 ? `${Math.round(returningUsers / totalActive * 100)}% of active` : ''} />
        <KpiCard label="Total active" value={fmtK(totalActive)} subtitle="this period" />
        <KpiCard label="Return rate" value={returnRate} subtitle="target > 60%" subtitleColor="pos" />
      </Grid4>

      <SectionEyebrow>Service mix</SectionEyebrow>
      <SectionTitle>Requests by service type</SectionTitle>
      <Card sx={{ marginBottom: '28px' }}>
        {services.length === 0 ? (
          <Box sx={{ textAlign: 'center', color: VALET.textMuted, fontSize: '13px', fontFamily: FONTS.sans, padding: '32px 0' }}>
            No requests in this period
          </Box>
        ) : (
          <>
            <CardTitle>Service mix — {fmtK(svcTotal)} total</CardTitle>

            {/* Bar chart */}
            <Box sx={{ height: 200, mb: '18px' }}>
              <Bar data={serviceChartData} options={chartOptions} />
            </Box>

            {/* Segmented bar */}
            <Box sx={{ display: 'flex', gap: '6px', height: '10px', borderRadius: '999px', overflow: 'hidden', mb: '14px' }}>
              {services.map((svc, i) => (
                <Box key={svc.name} sx={{ width: `${(svc.count / svcTotal * 100).toFixed(1)}%`, height: '100%', backgroundColor: SVC_COLORS[i % SVC_COLORS.length] }} />
              ))}
            </Box>

            {/* Legend */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '14px', mb: '18px' }}>
              {services.map((svc, i) => (
                <Box key={svc.name} sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontFamily: FONTS.sans, color: VALET.textSecondary }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '3px', backgroundColor: SVC_COLORS[i % SVC_COLORS.length], flexShrink: 0 }} />
                  <span>{svc.name} — {fmtK(svc.count)}</span>
                </Box>
              ))}
            </Box>

            {/* Service tiles */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', '@media (max-width: 700px)': { gridTemplateColumns: 'repeat(2, 1fr)' } }}>
              {services.map((svc, i) => (
                <Box key={svc.name} sx={{ padding: '14px 16px', backgroundColor: SVC_LIGHT[i % SVC_LIGHT.length], borderRadius: '12px', border: `1px solid ${SVC_COLORS[i % SVC_COLORS.length]}22` }}>
                  <Typography sx={{ ...TYPE.kpiLabel, color: SVC_COLORS[i % SVC_COLORS.length], mb: '6px' }}>
                    {svc.name}
                  </Typography>
                  <Typography sx={{ fontFamily: FONTS.display, fontSize: '22px', fontWeight: 500, letterSpacing: '-0.02em', color: SVC_COLORS[i % SVC_COLORS.length] }}>
                    {fmtK(svc.count)}
                  </Typography>
                  <Typography sx={{ fontFamily: FONTS.mono, fontSize: '10px', color: SVC_COLORS[i % SVC_COLORS.length], opacity: 0.75, mt: '2px' }}>
                    {(svc.count / svcTotal * 100).toFixed(1)}%
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Card>

      {(byHour.length > 0 || byDay.length > 0) && (
        <>
          <SectionEyebrow>Volume patterns</SectionEyebrow>
          <SectionTitle>When demand hits</SectionTitle>
          <Grid2>
            {byHour.length > 0 && (
              <Card>
                <CardTitle>By hour of day</CardTitle>
                <Box sx={{ height: 180 }}>
                  <Bar data={hourChartData} options={chartOptions} />
                </Box>
              </Card>
            )}
            {byDay.length > 0 && (
              <Card>
                <CardTitle>By day of week</CardTitle>
                <Box sx={{ height: 180 }}>
                  <Bar data={dayChartData} options={chartOptions} />
                </Box>
              </Card>
            )}
          </Grid2>
        </>
      )}
    </Box>
  );
}
